import { useMutation, useQueryClient } from '@tanstack/react-query';
import { moduleTools } from '@brimo/shared';
import { api } from '../../../../lib/api';
import type { ToolKey, ToolState } from '../../../../lib/session';
import { userKeys, type UsersTable } from './api';

/**
 * Tools per user (PKV M0, docs/dev/api-users.md „Tools per user"): the „Tools" column of „Nutzer und Rollen" and
 * the „Tools" section of the edit dialog → `PUT /users/:id/tools` with the changed tools only.
 */

export type ToolValue = 'yes' | 'no';
export type UserTools = Record<ToolKey, ToolValue>;

export interface UserToolsResponse {
  tools: UserTools;
  effective: Record<ToolKey, ToolState>;
}

/** Order and labels of the pills and switches. */
export const userToolItems: ReadonlyArray<{ key: ToolKey; label: string }> = [
  { key: 'dialer', label: 'Dialer' },
  { key: 'tickets', label: 'Tickets' },
  { key: 'deals', label: 'Deals' },
  { key: 'financing', label: 'Finanzierung' },
  { key: 'pkv', label: 'PKV' },
];

export const MODULE_OFF_TEXT = 'Modul aus';
export const SERVICING_LOCKED_TEXT =
  'Diese Mitgliedschaft wird von der übergeordneten Organisation verwaltet.';

/** False when a module that switches the tool off (P-09, `moduleTools`) is inactive in the organization. */
export function toolModuleActive(tool: ToolKey, activeModules: readonly string[]): boolean {
  for (const [module, tools] of Object.entries(moduleTools)) {
    if (tools.includes(tool) && !activeModules.includes(module)) return false;
  }
  return true;
}

export type ToolPillState = 'on' | 'off' | 'module_off';

export function toolPillState(value: ToolValue, moduleActive: boolean): ToolPillState {
  if (!moduleActive) return 'module_off';
  return value === 'yes' ? 'on' : 'off';
}

/** The body of `PUT /users/:id/tools`: only the tools whose value changed; null when nothing changed. */
export function toolsBody(before: UserTools, after: UserTools): Partial<UserTools> | null {
  const body: Partial<UserTools> = {};
  for (const { key } of userToolItems) if (before[key] !== after[key]) body[key] = after[key];
  return Object.keys(body).length > 0 ? body : null;
}

export function saveUserTools(id: string, body: Partial<UserTools>): Promise<UserToolsResponse> {
  return api<UserToolsResponse>(`/users/${id}/tools`, { method: 'PUT', body });
}

export function useSaveUserTools() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<UserTools> }) => saveUserTools(id, body),
    onSuccess: (result, { id }) => {
      queryClient.setQueryData<UsersTable>(userKeys.table(), (table) =>
        table
          ? {
              ...table,
              rows: table.rows.map((row) => (row.id === id ? { ...row, tools: result.tools } : row)),
            }
          : table,
      );
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}
